/**
 * In-app notifications for OE Condition events (Beta-1 plan P2-17). Writes `Hugo_Notifications`
 * docs (bell) from the client after a transition; the email side (WF8b/WF9, P2-18/P7-03/04) reads
 * the same docs. Recipients: `conditionRecipients.notificationsFor`.
 */
import { addDoc, collection, Timestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { notificationsFor, actedAsNotification } from '../assistants/drew/utils/conditionRecipients';

const COL = 'Hugo_Notifications';
const LINKS = {
  condition_new_l1: '/drew/rollup/review-queue',
  condition_l2_pending: '/drew/rollup/severity-calibration',
  condition_returned: '/drew/rollup/review-queue',
  condition_assigned: '/drew/role/dh',
  resolution_pending: '/drew/rollup/review-queue',
  condition_status: '/drew/rollup/trl-entry',
  admin_acted_as: '/drew/rollup/trl-entry',
};

/**
 * Write the notifications one transition produces. Fire-and-forget: failures are logged, never
 * thrown (the transition itself already succeeded).
 * @param {string} event   see conditionRecipients.js
 * @param {object} condition  the record as it was BEFORE the transition (plus id) — merge `after` for new values
 * @param {object} viewer   useConditionViewer() value (members, org, uid, name, actingFor)
 */
export async function notifyConditionEvent(event, condition, viewer, { note = '', after = {}, actedAs = null } = {}) {
  try {
    const c = { ...condition, ...after };
    const items = notificationsFor(event, c, { members: viewer?.members || [], org: viewer?.org || null, actorUid: viewer?.uid || null, note });
    if (actedAs && viewer?.actingFor) {
      const n = actedAsNotification(viewer.actingFor, viewer.keyboardName || viewer.name, actedAs, c);
      if (n) items.push(n);
    }
    if (items.length === 0) return 0;
    const batch = writeBatch(db);
    for (const n of items) {
      const link = c.id ? `${LINKS[n.type] || LINKS.condition_status}?condition=${c.id}` : LINKS[n.type] || null;
      batch.set(doc(collection(db, COL)), {
        userId: n.uid,
        type: n.type,
        message: n.message,
        link,
        organizationId: c.organizationId || viewer?.orgId || null,
        conditionId: c.conditionId || null,
        trlId: c.id || null,
        read: false,
        createdAt: Timestamp.now(),
      });
    }
    await batch.commit();
    return items.length;
  } catch (err) {
    console.warn('notifyConditionEvent:', err?.message || err);
    return 0;
  }
}

/** Survey published / assigned to a scope (P2-17, TICK C25). */
export async function notifySurveyPublished(survey, recipients, organizationId) {
  try {
    const batch = writeBatch(db);
    for (const uid of recipients) {
      batch.set(doc(collection(db, COL)), {
        userId: uid,
        type: 'survey_published',
        message: `New survey to take: ${survey.title}`,
        link: `/drew/surveys/${survey.id}`,
        organizationId: organizationId || survey.organizationId || null,
        read: false,
        createdAt: Timestamp.now(),
      });
    }
    await batch.commit();
  } catch (err) {
    console.warn('notifySurveyPublished:', err?.message || err);
  }
}

export { addDoc };
